/*
  fw-a11y.js — Feinste-Weine accessibility shims for Impulse 9.2.0 dialogs.
  Fixes three stock behaviours without editing theme.js (see #5 A11Y-01/02, #8 V2–V4):

    1. Focus return: Impulse's Modal stores `evt.currentTarget.setAttribute(...)`
       (= undefined) as the element to refocus, so focus is never returned on
       close; drawers opened without an event (cart drawer after add-to-cart)
       have no return target either. We restore focus to the dialog's trigger
       on close if Impulse left focus on <body>.
    2. Backward focus trap: Impulse's trapFocus only wraps Tab from the last
       focusable; Shift+Tab from the first escapes the dialog. We wrap it.
    3. Dialog semantics: role="dialog" / aria-modal / aria-labelledby on every
       .modal and .drawer (only PhotoSwipe had them).

  Impulse contract relied on (verified against theme.js — 3 Sep #27 review):
    - open state classes `modal--is-active` (Modal config) and
      `drawer--is-open` (Drawers config); stock hides closed dialogs with
      display:none (theme.css .modal/.drawer).
    - MODALS: Modal.open adds the class, dispatches `modalOpen` synchronously,
      and defers its trapFocus by 100 ms (theme.js:2528-2537) — at open time
      focus is still on the trigger.
    - DRAWERS: the order is REVERSED. Drawers.open runs theme.a11y.trapFocus
      FIRST — it focuses the drawer element itself (theme.js:193-199) — and
      only then dispatches `drawerOpen` (theme.js:2360-2366). Focus is already
      inside the drawer before any event or observer can run. The shim
      therefore never derives the trigger from event timing: it tracks the
      last focus/pointer target OUTSIDE any dialog and binds it per container
      at the open transition (see bindTrigger below).
    - Impulse's own close path refocuses a valid source ~500 ms after close
      (theme.js:2401, :2573); the shim acts at 600 ms and only if focus was
      left on <body>, so it never fights a restore Impulse performed itself.

  Out of scope (deliberate, recorded in docs/CUSTOM-SURFACE.md):
    - <tool-tip> (theme.js:3829-3905): signals open state via the
      data-tool-tip-open attribute, not a class this observer watches; its
      _close refocuses nothing (vendor gap — ledgered in #8). Extend
      attributeFilter + container detection if it is ever brought in scope.
    - PhotoSwipe (.pswp): ships its own dialog semantics and focus handling.
    - predictive-search (`is-active`): an inline panel, not a modal dialog.
  Headingless dialogs (video, password; quick-shop until its fetch lands)
  get a locale-safe aria-label="Dialog" fallback so role="dialog" is never
  unnamed (WCAG 4.1.2 — 6 Sep review); a heading found on a later open
  transition sets aria-labelledby, which outranks the fallback.
*/
(function () {
  'use strict';
  if (!document.querySelector || !window.MutationObserver || typeof WeakMap !== 'function') return;

  var OPEN = { modal: 'modal--is-active', drawer: 'drawer--is-open' };
  // Impulse runs its own close work (refocus of a valid source, transition
  // cleanup) inside setTimeout(..., 500) — theme.js:2573
  // ("}.bind(this), 500); // modal close css transition"). We restore focus
  // only after that attempt has had its chance; the margin absorbs timer
  // jitter. Single source of truth — the regression test reads this value
  // from window.fwA11y rather than encoding its own guess, and asserts it
  // clears the vendor's 500 ms.
  var RESTORE_DELAY_MS = 600;
  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])';
  var openContainers = new Set();  // currently open .modal/.drawer elements

  function visible(el) {
    if (!el) return false;
    if (el !== document.activeElement && el.offsetParent === null) return false;
    // visibility:hidden/collapse anywhere up the chain hides the element AND
    // makes browsers refuse .focus() on it — the mobile menu drawer's
    // collapsed submenus are exactly this (3 Sep keyboard pass, P2-d; stock
    // trapFocus shares the defect via its bare offsetParent filter,
    // theme.js:190-193 — ledgered in #8). A descendant re-enabled with
    // visibility:visible under a hidden ancestor is deliberately
    // over-excluded (the walk stops at the first hidden ancestor): dropping
    // a rare visible element from wrap targets is safe, focusing an
    // unfocusable one swallows the keystroke.
    if (window.getComputedStyle) {
      for (var n = el; n && n.nodeType === 1; n = n.parentElement) {
        var v = getComputedStyle(n).visibility;
        if (v === 'hidden' || v === 'collapse') return false;
      }
    }
    if (el.closest && el.closest('[aria-hidden="true"]')) return false;
    return true;
  }
  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return !el.disabled && visible(el);
    });
  }
  function isOpen(el) {
    return el.classList.contains(OPEN.modal) || el.classList.contains(OPEN.drawer);
  }
  function labelFor(container) {
    var h = container.querySelector('h1, h2, h3, .h1, .h2, .h3, .drawer__title, .modal__title');
    if (!h) {
      // No heading (video/password never get one; quick-shop's arrives only
      // after its fetch resolves, i.e. after the open transition): a dialog
      // announced as a dialog with NO name is a WCAG 4.1.2 failure this shim
      // would otherwise introduce (6 Sep review). "Dialog" is the same word
      // in German and English, so a static fallback is locale-safe. When a
      // heading appears later, semantics() re-runs on the next open and sets
      // aria-labelledby, which outranks aria-label in the accessible-name
      // computation — no removal needed.
      if (!container.hasAttribute('aria-label')) container.setAttribute('aria-label', 'Dialog');
      return;
    }
    if (!h.id) h.id = 'fw-dialog-title-' + Math.random().toString(36).slice(2, 8);
    container.setAttribute('aria-labelledby', h.id);
  }
  function semantics(container) {
    // role + label are static and safe on closed dialogs; aria-modal is NOT:
    // it claims "ignore everything outside me", so it is set only while the
    // dialog is open (see the observer below) and removed on close. Stock
    // hides closed dialogs with display:none (theme.css .modal/.drawer), so
    // a static aria-modal happened to be inert — but that is a stock detail
    // an upgrade may change; the open/close lifecycle is the safe contract.
    if (!container.hasAttribute('role')) container.setAttribute('role', 'dialog');
    // Retry until a real heading is found: the 'Dialog' aria-label fallback
    // does not stop the search, only a labelledby does (late quick-shop
    // headings upgrade the name on the next open transition).
    if (!container.hasAttribute('aria-labelledby')) labelFor(container);
  }

  // 3. static semantics on everything present now
  try {
    document.querySelectorAll('.modal, .drawer').forEach(semantics);
  } catch (e) { /* never break the storefront */ }

  // 1a. Track the last focus/pointer target OUTSIDE any dialog, and bind it
  // per container at the open transition. This is timing-independent: it
  // does not matter whether Impulse moves focus before or after its open
  // event (drawers do it before — theme.js:2360-2366). pointerdown covers
  // browsers that do not focus buttons on click (Safari); a pointerdown on a
  // non-focusable target CLEARS the memory, so clicking empty page never
  // leaves a stale trigger behind.
  var lastOutside = null;
  var containerTriggers = new WeakMap();
  function outsideDialogs(el) {
    return !!(el && el.closest && !el.closest('.modal, .drawer'));
  }
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (!t || t === document.body || t === document.documentElement) return;
    if (outsideDialogs(t)) lastOutside = t;
  }, true);
  document.addEventListener('pointerdown', function (e) {
    var t = e.target;
    if (!(t instanceof Element) || !outsideDialogs(t)) return;
    lastOutside = t.closest(FOCUSABLE); // null on non-focusable targets: clears
  }, true);
  function bindTrigger(el) {
    var a = document.activeElement;
    if (a && a !== document.body && a !== document.documentElement && outsideDialogs(a)) {
      // Modal path: open ran with focus still on the trigger.
      containerTriggers.set(el, a);
    } else if (a && a.closest && a.closest('.modal, .drawer')) {
      // Drawer path (trapFocus already moved focus into a dialog) or a
      // dialog→dialog handoff (quick-shop ATC → cart drawer): the return
      // target is the last focus outside ANY dialog — for the handoff that
      // is the card's trigger, which no event-time read could produce.
      containerTriggers.set(el, lastOutside);
    } else {
      // Programmatic open with focus on <body> (newsletter popup, age gate):
      // there is no trigger; restoring anything would teleport the user.
      containerTriggers.set(el, null);
    }
  }

  // 1b. observe open/close by class; restore focus after Impulse's own close work
  function onClosed(container) {
    var trigger = containerTriggers.get(container) || null;
    containerTriggers.delete(container);
    setTimeout(function () {
      try {
        if (openContainers.size) return;                       // another dialog took over
        var a = document.activeElement;
        var focusLost = !a || a === document.body || a === document.documentElement || container.contains(a);
        if (focusLost && trigger && document.body.contains(trigger) && visible(trigger)) {
          trigger.focus({ preventScroll: true });
        }
      } catch (e) { /* ignore */ }
    }, RESTORE_DELAY_MS);
  }
  var mo = new MutationObserver(function (records) {
    records.forEach(function (r) {
      var el = r.target;
      if (!(el instanceof Element)) return;
      if (!el.classList.contains('modal') && !el.classList.contains('drawer')) return;
      var was = (r.oldValue || '').split(/\s+/);
      var wasOpen = was.indexOf(OPEN.modal) !== -1 || was.indexOf(OPEN.drawer) !== -1;
      var nowOpen = isOpen(el);
      if (nowOpen && !wasOpen) { openContainers.add(el); bindTrigger(el); semantics(el); el.setAttribute('aria-modal', 'true'); }
      if (!nowOpen && wasOpen) { openContainers.delete(el); el.removeAttribute('aria-modal'); onClosed(el); }
    });
  });
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'], subtree: true, attributeOldValue: true });

  // 2. Shift+Tab wrap inside the open dialog (Impulse only wraps forwards)
  document.addEventListener('keydown', function (evt) {
    if (evt.key !== 'Tab' || !evt.shiftKey || !openContainers.size) return;
    var container = null;
    openContainers.forEach(function (c) { if (c.contains(document.activeElement)) container = c; });
    if (!container) return;
    var els = focusables(container);
    if (!els.length) return;
    if (document.activeElement === els[0] || document.activeElement === container) {
      evt.preventDefault();
      els[els.length - 1].focus();
    }
  }, true);

  // Test/debug handle (harmless in production): lets the CI regression and
  // the manual preview pass interrogate the shim instead of re-deriving its
  // constants and filters.
  window.fwA11y = {
    RESTORE_DELAY_MS: RESTORE_DELAY_MS,
    focusables: focusables,
    visible: visible,
    openContainers: openContainers
  };
})();
